// /app/frontend/src/pages/PublicPhotoWallView.jsx
/**
 * Public Photo Wall View - Read-only view for sharing on social media
 * Available only during the configured publication period
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Camera, Share, X, Calendar, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import api from '../lib/api';
import { getImageUrl } from '../lib/utils';
import PhotoWallMosaic from '../components/PhotoWallMosaic';

export default function PublicPhotoWallView() {
  const { id: drawId } = useParams();
  const navigate = useNavigate();
  
  const [wallInfo, setWallInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isWithinPeriod, setIsWithinPeriod] = useState(true);

  useEffect(() => {
    loadWallInfo();
  }, [drawId]);

  const loadWallInfo = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/public/draw/${drawId}/photo-wall`);
      setWallInfo(res.data);
      
      // Check if within publication period
      const now = new Date();
      const startDate = res.data.start_date ? new Date(res.data.start_date) : null;
      const endDate = res.data.end_date ? new Date(res.data.end_date) : null;
      
      if (startDate && now < startDate) {
        setIsWithinPeriod(false);
        setError(`Le mur photos sera disponible à partir du ${startDate.toLocaleDateString('fr-FR')}`);
      } else if (endDate && now > endDate) {
        setIsWithinPeriod(false);
        setError(`Le mur photos n'est plus disponible depuis le ${endDate.toLocaleDateString('fr-FR')}`);
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Mur photos non disponible');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    const shareUrl = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Mur Photos - ${wallInfo?.draw_title}`,
          text: 'Découvrez les photos de l\'événement !',
          url: shareUrl
        });
      } catch (e) {
        // User cancelled
      }
    } else {
      navigator.clipboard.writeText(shareUrl);
      toast.success('Lien copié !');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  if (error || !isWithinPeriod) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-black flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-white/10 backdrop-blur border-white/20">
          <CardContent className="p-8 text-center">
            {!isWithinPeriod ? (
              <Calendar className="h-16 w-16 text-yellow-400 mx-auto mb-4" />
            ) : (
              <X className="h-16 w-16 text-red-400 mx-auto mb-4" />
            )}
            <h2 className="text-xl font-bold text-white mb-2">
              {!isWithinPeriod ? 'Mur photos non disponible' : 'Erreur'}
            </h2>
            <p className="text-white/70">{error}</p>
            <Button 
              className="mt-6" 
              variant="outline"
              onClick={() => navigate('/')}
            >
              Retour à l'accueil
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-black flex flex-col">
      {/* Header */}
      <div className="bg-black/30 backdrop-blur border-b border-white/10 p-4">
        <div className="max-w-4xl mx-auto">
          {/* Logos Row */}
          <div className="flex items-center justify-center gap-4 mb-3">
            {wallInfo?.organizer_logo && (
              <img 
                src={getImageUrl(wallInfo.organizer_logo)} 
                alt={wallInfo?.organizer_name} 
                className="h-12 w-auto max-w-[120px] object-contain rounded-lg bg-white/10 p-1"
              />
            )}
            {wallInfo?.beneficiary_logo && wallInfo?.organizer_logo && (
              <span className="text-white/40 text-2xl font-light">×</span>
            )}
            {wallInfo?.beneficiary_logo && (
              <img 
                src={getImageUrl(wallInfo.beneficiary_logo)} 
                alt={wallInfo?.beneficiary_name} 
                className="h-12 w-auto max-w-[120px] object-contain rounded-lg bg-white/10 p-1"
              />
            )}
          </div>
          
          {/* Title & Share */}
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h1 className="text-white font-bold text-xl">{wallInfo?.draw_title}</h1>
              <p className="text-white/60 text-sm">
                {wallInfo?.organizer_name}
                {wallInfo?.beneficiary_name && ` • ${wallInfo.beneficiary_name}`}
              </p>
            </div>
            <Button 
              onClick={handleShare}
              variant="outline"
              className="bg-white/10 border-white/20 text-white hover:bg-white/20"
            >
              <Share className="h-4 w-4 mr-2" />
              Partager
            </Button>
          </div>
        </div>
      </div>

      {/* Photo Wall Mosaic */}
      <div className="flex-1 flex items-center justify-center p-4 md:p-8">
        <PhotoWallMosaic drawId={drawId} className="w-full max-w-3xl" />
      </div>

      {/* Footer - Powered by TIREO */}
      <div className="py-4 text-center">
        <div className="flex items-center justify-center gap-2 text-white/40 text-sm">
          <span>Propulsé par</span>
          <span 
            className="font-black text-lg bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent animate-pulse"
            style={{ fontFamily: 'Outfit' }}
          >
            TIREO
          </span>
        </div>
      </div>
    </div>
  );
}
